import type { EditorProject } from '../types';

const MAX_HISTORY = 50;

export interface UndoSlice {
  _undo: EditorProject[];
  _redo: EditorProject[];
  _skipHistory: boolean;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  loadProject: (project: EditorProject) => void;
}

/**
 * Wraps a Zustand store config to add undo/redo for changes to `project`.
 * Usage: create<EditorStore>(withUndo((set, get, api) => ({ ... })))
 */
export function withUndo<T extends { project: EditorProject } & UndoSlice>(
  config: (set: (partial: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void, get: () => T, api: any) => T,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (rawSet: any, get: () => T, api: any): T => {
    const trackedSet = (partial: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => {
      const prev = get().project;

      rawSet(partial, replace);

      const state = get();
      if (state._skipHistory) return;

      const next = state.project;
      if (prev !== next) {
        const undoStack = [...state._undo, prev];
        if (undoStack.length > MAX_HISTORY) undoStack.shift();
        rawSet({ _undo: undoStack, _redo: [] });
      }
    };

    const initialState = config(trackedSet, get, api);

    return {
      ...initialState,
      _undo: [] as EditorProject[],
      _redo: [] as EditorProject[],
      _skipHistory: false,

      undo: () => {
        const state = get();
        if (state._undo.length === 0) return;

        const undoStack = [...state._undo];
        const prev = undoStack.pop()!;

        rawSet({
          project: prev,
          _undo: undoStack,
          _redo: [...state._redo, state.project],
          _skipHistory: true,
        });
        rawSet({ _skipHistory: false });
      },

      redo: () => {
        const state = get();
        if (state._redo.length === 0) return;

        const redoStack = [...state._redo];
        const next = redoStack.pop()!;

        rawSet({
          project: next,
          _redo: redoStack,
          _undo: [...state._undo, state.project],
          _skipHistory: true,
        });
        rawSet({ _skipHistory: false });
      },

      canUndo: () => get()._undo.length > 0,
      canRedo: () => get()._redo.length > 0,

      loadProject: (project: EditorProject) => {
        rawSet({ project, _undo: [], _redo: [], _skipHistory: true });
        rawSet({ _skipHistory: false });
      },
    };
  };
}
